import {useFormik} from 'formik';
import * as Yup from 'yup';
import {
  CreateFundraiserNotificationDTO,
  CommunicationMediaType,
  CommunicationVideoStyle,
  useSendFundraiserCommunicationMessage,
} from '../../../entities';
import {AxiosError} from 'axios';

type UseSendMessageFormProps = {
  appealId: string;
  setOpen: (open: boolean) => void;
};

export const useSendMessageForm = ({appealId, setOpen}: UseSendMessageFormProps) => {
  const {sendMessageMutation} = useSendFundraiserCommunicationMessage(appealId);

  const formik = useFormik({
    initialValues: {
      subject: '',
      body: '',
      showButton: false,
      buttonText: '',
      buttonLink: '',
      mediaType: 'image' as CommunicationMediaType,
      imageData: null as any,
      imagePreview: null as string | null,
      videoUploadId: null as string | null,
      videoPreview: null as string | null,
      videoStyle: 'Circle' as CommunicationVideoStyle,
    },
    validationSchema: Yup.object({
      subject: Yup.string().max(256, 'Заголовок не должен превышать 256 символов').required('Заголовок обязателен'),
      body: Yup.string().max(1000, 'Текст не должен превышать 1000 символов').required('Текст уведомления обязателен'),
      buttonText: Yup.string().when('showButton', {
        is: true,
        then: (schema) =>
          schema.required('Введите текст кнопки').max(50, 'Текст кнопки не должен превышать 50 символов'),
      }),
      buttonLink: Yup.string().when('showButton', {
        is: true,
        then: (schema) => schema.required('Введите ссылку кнопки').url('Некорректная ссылка'),
      }),
    }),
    onSubmit: async (values) => {
      try {
        let media: CreateFundraiserNotificationDTO['media'] = null;
        if (values.mediaType === 'image' && values.imageData?.uploadId) {
          media = {type: 'image', imageUploadId: values.imageData.uploadId};
        } else if (values.mediaType === 'video' && values.videoUploadId) {
          media = {type: 'video', videoUploadId: values.videoUploadId, style: values.videoStyle};
        }

        const payload: CreateFundraiserNotificationDTO = {
          subject: values.subject,
          body: values.body,
          group: 'AppealsAndReports',
          button: values.showButton
            ? {
                text: values.buttonText,
                link: values.buttonLink,
              }
            : null,
          media,
        };

        await sendMessageMutation.mutateAsync(payload);
        setOpen(false);
      } catch (error: any) {
        console.error(error);
      }
    },
  });

  return {
    values: formik.values,
    errors: formik.errors,
    touched: formik.touched,
    handleChange: formik.handleChange,
    handleBlur: formik.handleBlur,
    setFieldValue: formik.setFieldValue,
    setFieldTouched: formik.setFieldTouched,
    resetForm: formik.resetForm,
    submitForm: formik.submitForm,
    isValid: formik.isValid,
    isLoading: sendMessageMutation.isPending,
    submitError: sendMessageMutation.error
      ? (sendMessageMutation.error as AxiosError<ApiError>).response?.data
      : undefined,
  };
};
